import { defineStore } from 'pinia'
import axios from 'axios'
import { useCookies } from 'vue3-cookies'

import { BASE_URL, AUTH_COOKIES, getBaseUrl } from '@/scripts/base'

const { cookies } = useCookies()

interface LoginPayload {
  username: string
  password: string
}

interface AuthState {
  user_by_APP: any | null
  PERM: any | null
  ERROR: any | null
  auth_error_message: string | null
  isInitialized: boolean
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user_by_APP: JSON.parse(localStorage.getItem('user_by_APP') || 'null'),
    PERM: JSON.parse(localStorage.getItem('permissions_by_APP') || 'null'),
    ERROR: null,
    auth_error_message: null,
    isInitialized: false,
  }),

  getters: {
    user: (state) => state.user_by_APP,
    isAuth: () => !!cookies.get(AUTH_COOKIES.access),
  },

  actions: {
    async initializeAuth() {
      try {
        const hasAccess = !!cookies.get(AUTH_COOKIES.access)
        const hasRefresh = !!cookies.get(AUTH_COOKIES.refresh)

        if (!hasAccess && hasRefresh) {
          await this.refreshToken()
        }

        if (this.inspectToken() && !this.user_by_APP) {
          await this.objectUser()
        }

        this.isInitialized = true
        return true
      } catch (error) {
        console.error('Auth initialization error:', error)
        this.isInitialized = true
        return false
      }
    },

    setError(error: any | null) {
      this.ERROR = error
    },

    updateToken(access: string, expireTime: number) {
      cookies.set(AUTH_COOKIES.access, access, expireTime)
    },

    updateRefreshToken(refresh: string, expireTime: number) {
      cookies.set(AUTH_COOKIES.refresh, refresh, expireTime)
    },

    updateUser(data: any) {
      localStorage.setItem('user_by_APP', JSON.stringify(data))
      this.user_by_APP = data
    },

    updatePermissions(data: any) {
      localStorage.setItem('permissions_by_APP', JSON.stringify(data))
      this.PERM = data
    },

    logoutUser() {
      cookies.remove(AUTH_COOKIES.access)
      cookies.remove(AUTH_COOKIES.refresh)

      localStorage.removeItem('user_by_APP')
      localStorage.removeItem('permissions_by_APP')

      this.user_by_APP = null
      this.PERM = null
      this.ERROR = null
      this.auth_error_message = null

      return true
    },

    updateAuthError(message: string | null) {
      this.auth_error_message = message
    },

    async obtainToken(data: LoginPayload) {
      const params = {
        username: data.username,
        password: data.password,
      }

      const response = await axios.post(getBaseUrl() + 'auth/token-admin/', params, {
        timeout: 10000,
      })

      if (response.data.access_token) {
        this.updateToken(response.data.access_token, 60 * 60 * 24 * 30)
      }

      if (response.data.refresh_token) {
        this.updateRefreshToken(response.data.refresh_token, 60 * 60 * 24 * 30)
      }

      this.updateAuthError(null)
      this.setError(null)

      return true
    },

    async refreshToken() {
      const refreshToken = cookies.get(AUTH_COOKIES.refresh)

      if (!refreshToken) {
        this.logoutUser()
        return false
      }

      try {
        const response = await axios.post(getBaseUrl() + 'auth/token/refresh/', {
          refresh_token: refreshToken,
        }, {
          timeout: 10000,
        })

        if (response.data.access_token) {
          this.updateToken(response.data.access_token, 60 * 60 * 24 * 30)
        }

        if (response.data.refresh_token) {
          this.updateRefreshToken(response.data.refresh_token, 60 * 60 * 24 * 30)
        }

        this.updateAuthError(null)

        return true
      } catch (error) {
        this.logoutUser()
        throw error
      }
    },

    inspectToken() {
      return !!cookies.get(AUTH_COOKIES.access)
    },

    async objectUser() {
      const response = await BASE_URL.get('/auth/user/')
      this.updateUser(response.data?.user || null)

      return true
    },

    setAuthError(message: string | null) {
      this.updateAuthError(message)
    },
  },
})